import { useState, type ReactNode } from "react";
import { HiLockClosed, HiRefresh, HiX } from "react-icons/hi";
import type { ExportOption, ExportOptionKind } from "../../types/exports";
import type {
  ExportDateRange,
  ExportPeriod,
} from "../../utils/exportDateRanges";
import {
  ExportConditionPopover,
  FIELD_LABELS,
  FIELD_ORDER,
} from "./ExportConditionPopover";
import { useOptionLabels } from "./ExportOptionSelect";
import { ExportPeriodPicker } from "./ExportPeriodPicker";

export interface ExportDraft {
  period: ExportPeriod;
  providerIds: number[];
  contractTypeIds: number[];
  teamIds: number[];
  workerIds: number[];
}

type OptionState = Record<ExportOptionKind, ExportOption[]>;

const DRAFT_KEYS = {
  providers: "providerIds",
  contract_types: "contractTypeIds",
  teams: "teamIds",
  workers: "workerIds",
} as const satisfies Record<ExportOptionKind, keyof ExportDraft>;

interface Props {
  draft: ExportDraft;
  onChange: (next: ExportDraft) => void;
  options: OptionState;
  loading: Record<ExportOptionKind, boolean>;
  errors: Partial<Record<ExportOptionKind, string>>;
  range: ExportDateRange | null;
  dateError: string | null;
  today: string;
  /** Right-hand side of the toolbar, e.g. the export button. */
  actions?: ReactNode;
}

function FilterChip({
  kind,
  ids,
  options,
  onRemove,
}: {
  kind: ExportOptionKind;
  ids: number[];
  options: ExportOption[];
  onRemove: () => void;
}) {
  const labels = useOptionLabels(options);
  const names = ids.map((id) => labels.get(id));
  const value =
    ids.length <= 2 && names.every(Boolean)
      ? names.join(", ")
      : `${ids.length.toLocaleString("de-DE")} ausgewählt`;
  const text = `${FIELD_LABELS[kind]}: ${value}`;
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
      <span className="max-w-[240px] truncate" title={text}>
        {text}
      </span>
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:bg-blue-800"
        aria-label={`Filter ${FIELD_LABELS[kind]} entfernen`}
        title="Diesen Filter entfernen"
      >
        <HiX className="h-3 w-3" aria-hidden="true" />
      </button>
    </span>
  );
}

export function ExportFilterBuilder({
  draft,
  onChange,
  options,
  loading,
  errors,
  range,
  dateError,
  today,
  actions,
}: Props) {
  // Rows the admin added but has not filled yet; rows with a selection are
  // always shown, so selections made elsewhere (cross-selling) appear too.
  const [openRows, setOpenRows] = useState<ExportOptionKind[]>([]);
  const selections: Record<ExportOptionKind, number[]> = {
    providers: draft.providerIds,
    contract_types: draft.contractTypeIds,
    teams: draft.teamIds,
    workers: draft.workerIds,
  };
  const rows = FIELD_ORDER.filter(
    (kind) =>
      kind === "providers" ||
      openRows.includes(kind) ||
      selections[kind].length > 0
  );
  const activeKinds = FIELD_ORDER.filter((kind) => selections[kind].length > 0);

  const withSelection = (
    base: ExportDraft,
    kind: ExportOptionKind,
    ids: number[]
  ): ExportDraft => {
    if (kind !== "providers") return { ...base, [DRAFT_KEYS[kind]]: ids };
    // Contract types must belong to a selected provider.
    const permittedTypes = new Set(
      options.contract_types
        .filter(
          (option) =>
            option.provider_id != null && ids.includes(option.provider_id)
        )
        .map((option) => option.id)
    );
    return {
      ...base,
      providerIds: ids,
      contractTypeIds: base.contractTypeIds.filter((id) =>
        permittedTypes.has(id)
      ),
    };
  };
  const select = (kind: ExportOptionKind, ids: number[]) =>
    onChange(withSelection(draft, kind, ids));
  const removeRow = (kind: ExportOptionKind) => {
    setOpenRows((previous) => previous.filter((row) => row !== kind));
    select(kind, []);
  };
  const replaceRow = (from: ExportOptionKind, to: ExportOptionKind) => {
    if (from === to) return;
    setOpenRows((previous) => [...previous.filter((row) => row !== from), to]);
    select(from, []);
  };
  const reset = () => {
    setOpenRows([]);
    onChange({
      ...draft,
      providerIds: [],
      contractTypeIds: [],
      teamIds: [],
      workerIds: [],
    });
  };

  return (
    <section className="space-y-2" aria-label="Export-Filter">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <ExportPeriodPicker
            period={draft.period}
            onChange={(period) => onChange({ ...draft, period })}
            range={range}
            dateError={dateError}
            today={today}
          />
          <ExportConditionPopover
            rows={rows}
            selections={selections}
            options={options}
            loading={loading}
            errors={errors}
            activeCount={activeKinds.length}
            onSelect={select}
            onAddRow={(kind) => setOpenRows((previous) => [...previous, kind])}
            onRemoveRow={removeRow}
            onReplaceRow={replaceRow}
            onReset={reset}
          />
          {activeKinds.length > 0 && (
            <button
              type="button"
              onClick={reset}
              className="inline-flex h-9 items-center gap-1 rounded-lg px-2 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
              title="Alle Filter entfernen"
            >
              <HiRefresh className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Zurücksetzen</span>
            </button>
          )}
        </div>
        {actions && (
          <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto">
            {actions}
          </div>
        )}
      </div>

      {dateError && (
        <p className="px-1 text-sm text-red-700 dark:text-red-300" role="alert">
          {dateError}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-1.5 px-1">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          Aktive Filter:
        </span>
        <span
          className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200"
          title="Es werden ausschließlich unterschriebene Verträge exportiert."
        >
          <HiLockClosed className="h-3 w-3" aria-hidden="true" />
          Status: Unterschrieben
        </span>
        {activeKinds.map((kind) => (
          <FilterChip
            key={kind}
            kind={kind}
            ids={selections[kind]}
            options={options[kind]}
            onRemove={() => select(kind, [])}
          />
        ))}
        <span className="text-xs text-gray-500 dark:text-gray-400">
          ·{" "}
          {draft.teamIds.length || draft.workerIds.length
            ? "Ausgewählte Teams und einzelne Mitarbeitende"
            : "Alle Mitarbeitenden Ihrer Organisation"}
          {draft.providerIds.length > 0 && !draft.contractTypeIds.length
            ? " · alle Vertragsarten der ausgewählten Anbieter"
            : ""}
        </span>
        {!draft.providerIds.length && (
          <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
            · Bitte mindestens einen Anbieter wählen.
          </span>
        )}
      </div>
    </section>
  );
}
